/**
 * sl-eglf.5.1 + sl-k94y.5.1: Locale helpers for marketplace `*I18n` rendering.
 *
 * Backend stores per-template/per-slot text as a `Record<locale, text>` keyed
 * by BCP-47 tag. The frontend picks one entry per render based on the active
 * i18n language plus the deployment-wide `defaultLocale` (sourced via
 * `useLocaleSettings`). This helper centralises the locale-chain so every
 * call site behaves the same way.
 *
 * Resolution order:
 *   1. Exact match for `language` (e.g. `de-DE`)
 *   2. Base of the requested language (e.g. `de` if `de-DE` was requested)
 *   3. `settings.defaultLocale` — deployment baseline for WORKSPACE/TENANT
 *      content
 *   4. `'en'` — last-resort safety net for GLOBAL/SYSTEM templates that are
 *      seeded with the canonical 'en' baseline regardless of the deployment's
 *      configured `defaultLocale`. Without this entry, a deployment with
 *      `defaultLocale='de'` viewing a GLOBAL `{en: '...'}` map would throw.
 *   5. Empty string — render layer decides whether to hide, placeholder, or
 *      leave the gap. `pickTitleStrict` upgrades this to a hard throw because
 *      titles are non-optional.
 *
 * Empty-after-trim values are skipped so a `{en: " "}` row falls through to
 * the next candidate instead of returning whitespace.
 *
 * SOLID:
 * - S: Only locale picking; nothing in this file fetches, validates, or
 *      mutates description maps.
 */

export interface LocaleSettings {
  readonly availableLocales: ReadonlyArray<string>;
  readonly defaultLocale: string;
}

function buildCandidateChain(
  language: string | undefined,
  defaultLocale: string
): string[] {
  const seen = new Set<string>();
  const chain: string[] = [];
  const push = (candidate: string | undefined) => {
    if (!candidate) return;
    if (seen.has(candidate)) return;
    seen.add(candidate);
    chain.push(candidate);
  };

  if (language) {
    push(language);
    push(language.split('-')[0]);
  }
  push(defaultLocale);
  // sl-k94y.5.1: 'en' as terminal fallback covers GLOBAL/SYSTEM templates that
  // are guaranteed to have an 'en' baseline by the backend validators —
  // independent of what the deployment's defaultLocale is.
  push('en');
  return chain;
}

export function pickDescription(
  descriptionsI18n: Record<string, string> | null | undefined,
  language: string | undefined,
  settings: LocaleSettings
): string {
  if (!descriptionsI18n) return '';
  for (const candidate of buildCandidateChain(
    language,
    settings.defaultLocale
  )) {
    const value = descriptionsI18n[candidate];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return '';
}

// sl-k94y.1.1: titles share the locale-map shape + resolution chain with
// descriptions. Titles are required (the publish boundary enforces a non-empty
// `defaultLocale` entry), so callers that want a fail-loud read use
// `pickTitleStrict`. `pickTitle` stays graceful for callers that want an empty
// string as the natural skeleton (e.g. while `useLocaleSettings` is loading).
export const pickTitle = pickDescription;

export class MissingTitleDefaultLocale extends Error {
  constructor() {
    super(
      `MissingTitleDefaultLocale: titlesI18n has no non-empty entry for the deployment's defaultLocale`
    );
    this.name = 'MissingTitleDefaultLocale';
  }
}

export function pickTitleStrict(
  titlesI18n: Record<string, string> | null | undefined,
  language: string | undefined,
  settings: LocaleSettings
): string {
  const value = pickDescription(titlesI18n, language, settings);
  if (value.length === 0) {
    throw new MissingTitleDefaultLocale();
  }
  return value;
}

// sl-k94y.6.1 / .6.2: slot labels are REQUIRED multi-locale maps; the seed
// + publish validators enforce a non-empty `en` baseline. Renderer therefore
// fails loud (Error-Boundary catches it in dev; production surfaces a broken
// slot that flags data corruption) instead of silently falling back to the
// machine `slot.name`. The `slotName` argument feeds the error message so the
// boundary can point at the offending field.
export class MissingLabelDefaultLocale extends Error {
  constructor(slotName: string) {
    super(
      `MissingLabelDefaultLocale: slot "${slotName}" has no labelsI18n entry for the default locale`
    );
    this.name = 'MissingLabelDefaultLocale';
  }
}

function buildSlotCandidateChain(language: string | undefined): string[] {
  const seen = new Set<string>();
  const candidates: string[] = [];
  const push = (candidate: string | undefined) => {
    if (!candidate || seen.has(candidate)) return;
    seen.add(candidate);
    candidates.push(candidate);
  };
  if (language) {
    push(language);
    push(language.split('-')[0]);
  }
  push('en');
  return candidates;
}

// sl-k94y.6.1 / .6.2: slot labels keep their existing 'en'-baseline contract
// (the seed + publish validators enforce this independent of the deployment's
// `defaultLocale`). This helper signature is unchanged by .5.1 — the
// `defaultLocale` chain doesn't apply to slot labels because slots are
// schema-level data, not user-authored copy.
export function pickSlotLabel(
  labelsI18n: Readonly<Record<string, string>> | null | undefined,
  language: string | undefined,
  slotName: string
): string {
  if (labelsI18n) {
    for (const candidate of buildSlotCandidateChain(language)) {
      const value = labelsI18n[candidate];
      if (typeof value === 'string' && value.trim().length > 0) return value;
    }
  }
  throw new MissingLabelDefaultLocale(slotName);
}

export function pickSlotDescription(
  descriptionsI18n: Readonly<Record<string, string>> | null | undefined,
  description: string | null | undefined,
  language: string | undefined
): string {
  if (descriptionsI18n) {
    for (const candidate of buildSlotCandidateChain(language)) {
      const value = descriptionsI18n[candidate];
      if (typeof value === 'string' && value.trim().length > 0) return value;
    }
  }
  return typeof description === 'string' ? description : '';
}

// sl-k94y.6.1: pure-context (sync, no i18n hook available) variant — used by
// shape providers that build NodeInputField/NodeOutputField scalars before the
// renderer mounts. Same fail-loud contract as the locale-aware picker, just
// pinned to the `en` baseline. Replacing the previous `?? slot.name` fallback:
// the validator enforces `en` at write time, so reaching this helper without
// a baseline is data corruption, not a UX edge-case.
export function requireDefaultSlotLabel(slot: {
  readonly labelsI18n: Readonly<Record<string, string>>;
  readonly name: string;
}): string {
  const en = slot.labelsI18n['en'];
  if (typeof en === 'string' && en.trim().length > 0) return en;
  throw new MissingLabelDefaultLocale(slot.name);
}
